import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import useEscapeKey from "@hooks/useEscapeKey";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { useTheme } from "@hooks/useTheme";
import { uppercase } from "@utils/common";
import { OutlineButton } from "@components/Buttons";
import { useClientConfig } from "@contexts/ClientConfigContext";
import { PermissionContext } from "@contexts/PermissionContext";
import { CVE_LINK } from "@constants/index";
import { DEFAULT_ITEMS_PER_PAGE, ITEMS_PER_PAGE_KEY } from "@constants/table";
import TableWithPagination from "../Tables/TableWithPagination";
import {
  fetchVexAttachments,
  fetchVexList,
  fetchVexSnapshot,
  fetchVexVersionAnalysis,
  updateVex,
  updateVexSnapshot,
  uploadVexAttachments,
  uploadVexSnapshotAttachments,
  deleteVexAttachment,
  vexSnapshotQueryKey,
} from "@apis/vex";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import {
  VEX_JUSTIFICATION_OPTIONS,
  VEX_SNAPSHOT_SOURCE,
  VEX_STATUS,
  VEX_STATUS_OPTIONS,
  computeVexProgress,
  hasFrozenSnapshotParentContext,
  isVexEditableVerificationStatus,
  mergeVexListByCve,
  shouldPreferVex,
  vexListKey,
} from "@constants/vex";
import {
  CveDetailPanel,
  CveListPanel,
  CveReviewPanel,
  VexProgressChip,
  severityRank,
} from "./SecurityInformation";

const emptyForm = {
  vexStatus: VEX_STATUS.AFFECTED,
  justification: "",
  additionalNote: "",
  files: [],
};

const toNumericScore = (score) => {
  const n = Number(score);
  return Number.isFinite(n) ? n : -1;
};

/**
 * Normalize securities for the left CVE list.
 * Dedupe by cve|cvss_version (same finding key as o2_vex / BE upsert) so
 * CycloneDX/SPDX duplicate entries do not render twice or break selection.
 * Keeps the first occurrence when keys collide.
 * Sorted by severity (critical → low), then score desc, then CVE id.
 */
const normalizeCveItems = (data) => {
  if (!Array.isArray(data)) return [];
  const seen = new Set();
  const items = [];
  data.forEach((item, index) => {
    const cve = (item?.cve || item?.cveId || "").trim();
    const cvssVersion = String(item?.cvssVersion ?? item?.cvss_version ?? "").trim();
    const key = vexListKey(cve, cvssVersion);
    if (key && key !== "|") {
      if (seen.has(key)) return;
      seen.add(key);
    }
    items.push({
      ...item,
      key: key && key !== "|" ? key : `cve-${index}`,
      cve,
      cvssVersion,
      cvss_version: cvssVersion,
      severity: item?.severity,
      score: item?.score,
    });
  });
  items.sort((a, b) => {
    const bySeverity = severityRank(a.severity) - severityRank(b.severity);
    if (bySeverity !== 0) return bySeverity;
    const byScore = toNumericScore(b.score) - toNumericScore(a.score);
    if (byScore !== 0) return byScore;
    return String(a.cve || "").localeCompare(String(b.cve || ""));
  });
  return items;
};

const buildVexMap = (list) => {
  const map = new Map();
  if (!Array.isArray(list)) return map;
  list.forEach((vex) => {
    const key = vexListKey(vex?.cve, vex?.cvssVersion);
    if (!key || key === "|") return;
    const existing = map.get(key);
    if (!existing || shouldPreferVex(existing, vex)) {
      map.set(key, vex);
    }
  });
  return map;
};

const SecurityInformationModal = ({
  show,
  onClose,
  data,
  allowVexReview = false,
  readOnlyVexReview = false,
  /** Frozen snapshot context; uses GET/PUT /vex/verification-report | /merged-sbom | /product */
  frozenVexSnapshot = null,
  /** Permission result supplied by the owning report/merge/product screen. */
  editableFrozenVexSnapshot = false,
  /** Row PK of the opened component within that snapshot (see §5.7) */
  frozenVexComponentSeq = null,
  scanToolCd,
  verificationSeq,
  rowVexContexts = [],
  componentName,
  componentVersion,
  projectManagerId,
  verificationStatusCd,
  "data-testid": dataTestId = "security-information-modal",
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const queryClient = useQueryClient();
  const clientConfig = useClientConfig();
  const { canEditVex } = useContext(PermissionContext) || {};
  useEscapeKey(onClose, show);

  const securityItems = useMemo(() => normalizeCveItems(data), [data]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);

  // Table mode (VEX off) pagination state
  const [tableData, setTableData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(
    ITEMS_PER_PAGE_KEY.SECURITY_INFORMATION_MODAL,
    DEFAULT_ITEMS_PER_PAGE,
  );

  const isVexOn = Boolean(clientConfig?.isClientConfigEnabled?.("useVex"));
  const liveVexContexts = useMemo(() => {
    if (Array.isArray(rowVexContexts) && rowVexContexts.length > 0) {
      return rowVexContexts;
    }
    if (scanToolCd && verificationSeq != null && verificationSeq !== "") {
      return [{ scanToolCd, verificationSeq, projectManagerId, verificationStatusCd }];
    }
    return [];
  }, [rowVexContexts, scanToolCd, verificationSeq, projectManagerId, verificationStatusCd]);

  const canEditLiveVex =
    isVexEditableVerificationStatus(verificationStatusCd, scanToolCd) ||
    liveVexContexts.some((ctx) =>
      isVexEditableVerificationStatus(ctx.verificationStatusCd, ctx.scanToolCd),
    );

  const hasVexContext = liveVexContexts.length > 0;
  const hasFrozenSnapshot = Boolean(frozenVexSnapshot?.source);
  const hasFrozenParentContext = hasFrozenSnapshotParentContext(frozenVexSnapshot);
  // Review panel: live verification (SBOM Review / any Ready), status page, or frozen snapshot
  const canShowReview = Boolean(
    isVexOn &&
      ((hasFrozenSnapshot && hasFrozenParentContext) ||
        (hasVexContext && ((allowVexReview && canEditLiveVex) || readOnlyVexReview))),
  );
  // Approve / attach: live permissions remain unchanged; snapshot permission belongs to its owner screen.
  // Verification-report snapshots sync to live VEX — DONE gate applied after frozenVexQuery loads.
  const canEditReviewBase = Boolean(
    canShowReview &&
      !readOnlyVexReview &&
      (hasFrozenSnapshot
        ? editableFrozenVexSnapshot
        : canEditVex?.(projectManagerId)),
  );

  const statusOptions = useMemo(
    () =>
      VEX_STATUS_OPTIONS.map((opt) => ({
        ...opt,
        label: t(`status.vex-status-${opt.value.toLowerCase().replace(/_/g, "-")}`, opt.label),
      })),
    [t],
  );

  const justificationOptions = useMemo(
    () =>
      VEX_JUSTIFICATION_OPTIONS.map((opt) => ({
        ...opt,
        label: t(`status.vex-justification-${opt.value.replace(/_/g, "-")}`, opt.label),
      })),
    [t],
  );

  const vexListQuery = useQuery({
    queryKey: [
      queryKeys.vexList,
      liveVexContexts[0]?.scanToolCd,
      liveVexContexts[0]?.verificationSeq,
      componentName ?? "",
      componentVersion ?? "",
    ],
    queryFn: () =>
      fetchVexList({
        scanToolCd: liveVexContexts[0].scanToolCd,
        verificationSeq: liveVexContexts[0].verificationSeq,
        componentName,
        componentVersion,
      }),
    enabled:
      show &&
      canShowReview &&
      hasVexContext &&
      !hasFrozenSnapshot &&
      liveVexContexts.length === 1,
  });

  const rowVexListQueries = useQueries({
    queries: liveVexContexts.map((ctx) => ({
      queryKey: [
        queryKeys.vexList,
        ctx.scanToolCd,
        ctx.verificationSeq,
        componentName ?? "",
        componentVersion ?? "",
      ],
      queryFn: () =>
        fetchVexList({
          scanToolCd: ctx.scanToolCd,
          verificationSeq: ctx.verificationSeq,
          componentName,
          componentVersion,
        }),
      enabled:
        show &&
        canShowReview &&
        hasVexContext &&
        !hasFrozenSnapshot &&
        liveVexContexts.length > 1,
    })),
  });

  const mergedLiveVexList = useMemo(() => {
    if (hasFrozenSnapshot) return [];
    if (liveVexContexts.length <= 1) {
      return vexListQuery.data?.list ?? [];
    }
    const combined = rowVexListQueries.flatMap((query) =>
      Array.isArray(query.data?.list) ? query.data.list : [],
    );
    return mergeVexListByCve(combined);
  }, [
    hasFrozenSnapshot,
    liveVexContexts.length,
    vexListQuery.data?.list,
    rowVexListQueries,
  ]);

  const frozenVexQuery = useQuery({
    queryKey: vexSnapshotQueryKey(frozenVexSnapshot, frozenVexComponentSeq),
    queryFn: () => fetchVexSnapshot(frozenVexSnapshot, frozenVexComponentSeq),
    enabled: show && canShowReview && hasFrozenParentContext && frozenVexComponentSeq != null,
  });

  // Verification report VEX writes back to live — only editable when source verification is DONE.
  const canEditReview = Boolean(
    canEditReviewBase &&
      (frozenVexSnapshot?.source !== VEX_SNAPSHOT_SOURCE.VERIFICATION_REPORT ||
        frozenVexQuery.data?.verificationStatusCd === "DONE"),
  );

  const snapshotVexList = useMemo(() => {
    if (Array.isArray(frozenVexQuery.data?.list)) return frozenVexQuery.data.list;
    if (Array.isArray(frozenVexQuery.data)) return frozenVexQuery.data;
    return [];
  }, [frozenVexQuery.data]);

  const vexMap = useMemo(() => {
    if (hasFrozenSnapshot) {
      return buildVexMap(snapshotVexList);
    }
    return buildVexMap(mergedLiveVexList);
  }, [hasFrozenSnapshot, snapshotVexList, mergedLiveVexList]);

  const cveItems = useMemo(() => {
    return securityItems.map((item) => {
      const vex = vexMap.get(item.key);
      return vex
        ? {
            ...item,
            vexSeq: vex.vexSeq,
            vexStatus: vex.vexStatus,
            justification: vex.justification,
            additionalNote: vex.additionalNote,
            fileGroupNo: vex.fileGroupNo || 0,
            componentName: vex.componentName ?? item.componentName,
            componentVersion: vex.componentVersion ?? item.componentVersion,
            severity: item.severity ?? vex.severity,
            score: item.score ?? vex.score,
          }
        : item;
    });
  }, [securityItems, vexMap]);

  const selectedItem = useMemo(
    () => cveItems.find((item) => item.key === selectedKey) || cveItems[0] || null,
    [cveItems, selectedKey],
  );

  const detailCve = selectedItem?.cve || "";
  const modalComponentName = componentName ?? securityItems[0]?.componentName ?? "";
  const modalComponentVersion = componentVersion ?? securityItems[0]?.componentVersion ?? "";

  const modalCveIds = useMemo(() => {
    const seen = new Set();
    const ids = [];
    securityItems.forEach((item) => {
      const cve = (item?.cve || "").trim();
      if (cve && !seen.has(cve)) {
        seen.add(cve);
        ids.push(cve);
      }
    });
    return ids;
  }, [securityItems]);

  const versionAnalysisQuery = useQuery({
    queryKey: [
      queryKeys.vexVersionAnalysis,
      modalCveIds,
      modalComponentName,
      modalComponentVersion,
    ],
    queryFn: () =>
      fetchVexVersionAnalysis({
        cves: modalCveIds,
        componentName: modalComponentName,
        componentVersion: modalComponentVersion,
      }),
    enabled: show && isVexOn && modalCveIds.length > 0,
  });

  const attachmentsQuery = useQuery({
    queryKey: [queryKeys.getFileInfo, selectedItem?.fileGroupNo],
    queryFn: () => fetchVexAttachments(selectedItem.fileGroupNo),
    enabled: show && canShowReview && Boolean(selectedItem?.fileGroupNo),
  });

  useEffect(() => {
    if (!show) return;
    setSelectedKey(securityItems[0]?.key ?? null);
    setForm(emptyForm);
  }, [show, data]);

  useEffect(() => {
    if (show) {
      setCurrentPage(1);
    }
  }, [show, data]);

  useEffect(() => {
    if (!securityItems.length) {
      setTableData([]);
      setTotalPages(1);
      return;
    }

    const total = securityItems.length;
    const pages = Math.ceil(total / itemsPerPage) || 1;
    setTotalPages(pages);

    const validPage = Math.min(currentPage, pages);
    if (validPage !== currentPage) {
      setCurrentPage(validPage);
      return;
    }

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setTableData(securityItems.slice(startIndex, endIndex));
  }, [securityItems, currentPage, itemsPerPage]);

  useEffect(() => {
    if (!selectedItem) return;

    setForm({
      vexStatus: selectedItem.vexStatus || VEX_STATUS.AFFECTED,
      justification: selectedItem.justification || "",
      additionalNote: selectedItem.additionalNote || "",
      files: [],
    });
  }, [
    selectedItem?.key,
    selectedItem?.vexSeq,
    selectedItem?.vexStatus,
    selectedItem?.justification,
    selectedItem?.additionalNote,
  ]);

  // Snapshot of the persisted row — used to detect whether the user changed anything.
  const initialForm = useMemo(
    () => ({
      vexStatus: selectedItem?.vexStatus || VEX_STATUS.AFFECTED,
      justification: selectedItem?.justification || "",
      additionalNote: selectedItem?.additionalNote || "",
    }),
    [
      selectedItem?.vexStatus,
      selectedItem?.justification,
      selectedItem?.additionalNote,
      selectedItem?.vexSeq,
      selectedItem?.key,
    ],
  );

  const selectedVersionAnalysis = useMemo(() => {
    if (!detailCve || !Array.isArray(versionAnalysisQuery.data)) {
      return null;
    }
    return versionAnalysisQuery.data.find((item) => item?.cveId === detailCve) || null;
  }, [versionAnalysisQuery.data, detailCve]);

  const detailEvidence = useMemo(() => {
    const analysis = selectedVersionAnalysis;
    return {
      componentName: modalComponentName,
      installedVersion: analysis?.installedVersion ?? modalComponentVersion ?? "",
      matches: analysis?.matches || [],
      withinAffectedRange: analysis?.withinAffectedRange ?? null,
    };
  }, [selectedVersionAnalysis, modalComponentName, modalComponentVersion]);

  const approveMutation = useMutation({
    mutationFn: async ({ vexSeq, body, files, fileGroupNo }) => {
      const updated = hasFrozenSnapshot
        ? await updateVexSnapshot(frozenVexSnapshot.source, vexSeq, body)
        : await updateVex(vexSeq, body);
      if (files?.length) {
        const upload = hasFrozenSnapshot
          ? uploadVexSnapshotAttachments
          : uploadVexAttachments;
        await upload({
          source: frozenVexSnapshot?.source,
          vexSeq,
          fileGroupNo: updated?.fileGroupNo || fileGroupNo || 0,
          files,
        });
      }
      return updated;
    },
    onSuccess: async () => {
      showSuccessToast(t("status.vex-approve-success"));
      setForm((prev) => ({ ...prev, files: [] }));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.vexList] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.vexProgress] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.vexSnapshot] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.getFileInfo] }),
      ]);
    },
    onError: (error) => {
      const code = error?.response?.data?.code || error?.vexCode;
      const message = code
        ? t(`errorcode.${code}`, {
            defaultValue: error?.response?.data?.message || t("status.vex-approve-error"),
          })
        : error?.response?.data?.message || t("status.vex-approve-error");
      showErrorToast(message);
    },
  });

  const deleteAttachmentMutation = useMutation({
    mutationFn: deleteVexAttachment,
    onSuccess: async () => {
      showSuccessToast(t("common.file-delete-success"));
      await queryClient.invalidateQueries({ queryKey: [queryKeys.getFileInfo] });
    },
    onError: (error) => {
      const message = error?.response?.data?.message || t("common.file-delete-error");
      showErrorToast(message);
    },
    onSettled: () => {
      setDeletingAttachmentId(null);
    },
  });

  const handleApprove = useCallback(() => {
    if (!canEditReview || !selectedItem?.vexSeq) {
      if (!selectedItem?.vexSeq) {
        showErrorToast(t("status.vex-approve-error"));
      }
      return;
    }

    const vexStatus = form.vexStatus || "";
    if (!vexStatus) {
      showErrorToast(t("status.vex-status-required"));
      return;
    }
    if (vexStatus === VEX_STATUS.NOT_AFFECTED && !form.justification) {
      showErrorToast(t("status.vex-justification-required-error"));
      return;
    }
    if ((form.additionalNote || "").length > 1000) {
      showErrorToast(t("status.vex-notes-max-error"));
      return;
    }

    approveMutation.mutate({
      vexSeq: selectedItem.vexSeq,
      fileGroupNo: selectedItem.fileGroupNo || 0,
      files: form.files || [],
      body: {
        vexStatus,
        justification: vexStatus === VEX_STATUS.NOT_AFFECTED ? form.justification : null,
        additionalNote: form.additionalNote || null,
      },
    });
  }, [approveMutation, canEditReview, form, selectedItem, t]);

  const handleDeleteExistingAttachment = useCallback(
    (fileInfoSeq) => {
      if (!canEditReview || !fileInfoSeq) return;
      setDeletingAttachmentId(fileInfoSeq);
      deleteAttachmentMutation.mutate(fileInfoSeq);
    },
    [canEditReview, deleteAttachmentMutation],
  );

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1);
  };

  const tableColumns = ["cve", "cvss_version", "severity", "score"];

  const tableColumnConfig = {
    cve: {
      style: {
        width: "30%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("status.cve"),
      customRender: (value, row) => (
        <a
          href={`${CVE_LINK}/${row.cve}`}
          target="_blank"
          rel="noopener noreferrer"
          className={`${colors.textComparisonLink} ${colors.hoverTextComparisonLink} underline`}
        >
          {row.cve}
        </a>
      ),
    },
    cvss_version: {
      style: {
        width: "20%",
        whiteSpace: "normal",
        textAlign: "center",
        overflowWrap: "initial",
      },
      headerRender: () => t("status.cvss-version"),
    },
    severity: {
      style: {
        width: "25%",
        whiteSpace: "normal",
        textAlign: "center",
        overflowWrap: "initial",
      },
      headerRender: () => t("status.severity"),
      customRender: (value, row) => <div className="capitalize">{row.severity}</div>,
    },
    score: {
      style: {
        width: "25%",
        whiteSpace: "normal",
        textAlign: "center",
        overflowWrap: "initial",
      },
      headerRender: () => t("status.score"),
    },
  };

  if (!show) {
    return null;
  }

  const detailCveId = selectedItem?.cve || "-";
  const componentProgress = hasFrozenSnapshot
    ? computeVexProgress(snapshotVexList)
    : liveVexContexts.length > 1
      ? computeVexProgress(mergedLiveVexList)
      : vexListQuery.data?.progress;
  const isLiveVexLoading =
    liveVexContexts.length > 1
      ? rowVexListQueries.some((query) => query.isLoading)
      : vexListQuery.isLoading;
  const isVexReviewLoading = hasFrozenSnapshot
    ? frozenVexQuery.isLoading
    : isLiveVexLoading;

  const modalContent = (
    <div
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start xl:justify-center backdrop-blur-[1px] overflow-x-auto p-4`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div
        className={`${colors.bgModal} ${
          isVexOn ? "h-[85vh] max-h-[85vh]" : "max-h-[85vh]"
        } w-[1200px] min-w-[1100px] flex-shrink-0 rounded-lg shadow-xl flex flex-col overflow-hidden`}
        data-testid={dataTestId}
      >
        <div className="px-6 py-4 flex-shrink-0" data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>
                {uppercase(t("status.security-information-modal-title"))}
              </h2>
              {canShowReview && (
                <VexProgressChip
                  progress={componentProgress}
                  labelKey="status.vex-progress-processed"
                  titleKey="status.vex-progress-processed-tooltip"
                  showInfoTooltip
                  data-testid={`${dataTestId}-component-progress`}
                />
              )}
            </div>
          </div>
        </div>

        <div
          className={`flex flex-col px-6 min-h-0 pb-2 ${
            isVexOn ? "flex-1 overflow-hidden" : "overflow-y-auto"
          }`}
          data-testid={`${dataTestId}-content`}
        >
          {!isVexOn ? (
            !securityItems.length ? (
              <div className={`text-center py-10 ${colors.textPrimary}`} data-testid={`${dataTestId}-no-data`}>
                {t("common.no-data-to-display")}
              </div>
            ) : (
              <TableWithPagination
                columns={tableColumns}
                data={tableData}
                columnConfig={tableColumnConfig}
                maxRowsPerPage={itemsPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
                totalData={securityItems.length}
                showPagination={true}
                showItemsPerPageSelector={true}
                onItemsPerPageChange={handleItemsPerPageChangeWithReset}
                tableStyle="component-table table-fixed"
                loading={false}
                stickyHeader={true}
                scrollY="calc(85vh - 240px)"
                dataTestId={`${dataTestId}-table`}
              />
            )
          ) : !cveItems.length ? (
            <div className={`text-center py-10 ${colors.textPrimary}`} data-testid={`${dataTestId}-no-data`}>
              {t("common.no-data-to-display")}
            </div>
          ) : (
            <div className="grid grid-cols-[280px_minmax(0,1fr)] gap-4 min-h-0 flex-1 h-full overflow-hidden">
              <CveListPanel
                items={cveItems}
                selectedKey={selectedItem?.key}
                onSelect={(item) => setSelectedKey(item.key)}
                data-testid={`${dataTestId}-list`}
              />

              <div
                className={`flex min-h-0 h-full flex-col overflow-hidden rounded-lg border ${colors.borderPrimary} ${colors.bgInput} p-4`}
                data-testid={`${dataTestId}-detail-region`}
              >
                <div className="mb-4 flex flex-wrap items-center gap-3 flex-shrink-0">
                  <h3 className={`text-xl font-semibold ${colors.textPrimary}`} data-testid={`${dataTestId}-selected-cve`}>
                    {detailCveId && detailCveId !== "-" ? (
                      <a
                        href={`${CVE_LINK}/${detailCveId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${colors.textComparisonLink} ${colors.hoverTextComparisonLink} underline`}
                      >
                        {detailCveId}
                      </a>
                    ) : (
                      detailCveId
                    )}
                  </h3>
                </div>

                <div
                  className={`grid min-h-0 flex-1 gap-4 overflow-hidden ${
                    canShowReview ? "grid-cols-2" : "grid-cols-1"
                  }`}
                >
                  <div className="min-h-0 h-full overflow-hidden">
                    <CveDetailPanel
                      cveItem={selectedItem}
                      evidence={detailEvidence}
                      loading={canShowReview && isVexReviewLoading}
                      versionAnalysisLoading={versionAnalysisQuery.isFetching}
                      data-testid={`${dataTestId}-detail`}
                    />
                  </div>
                  {canShowReview && (
                    <div className="min-h-0 h-full overflow-hidden">
                      <CveReviewPanel
                        form={form}
                        onChange={setForm}
                        statusOptions={statusOptions}
                        justificationOptions={justificationOptions}
                        existingAttachments={attachmentsQuery.data || []}
                        onDeleteExistingAttachment={handleDeleteExistingAttachment}
                        deletingAttachmentId={deletingAttachmentId}
                        onApprove={handleApprove}
                        approving={approveMutation.isPending}
                        disabled={!canEditReview || !selectedItem?.vexSeq}
                        initialForm={initialForm}
                        data-testid={`${dataTestId}-review`}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={`px-6 py-4 flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
          <div className="flex items-center justify-end gap-3">
            <OutlineButton data-testid={`${dataTestId}-close-button`} onClick={onClose}>
              {t("common.close-button")}
            </OutlineButton>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SecurityInformationModal;
